import React, { useMemo } from 'react';

import { Button, Divider, Grid, Group, LoadingOverlay, Paper, Switch, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import useUpdateGym from 'hooks/gym/useUpdateGym';
import { GymRetrieve } from 'types';
import notificationHandler from 'utils/notifications';

type FormValues = Pick<
  GymRetrieve,
  'athleteAppHideReservedAthletes' | 'athleteAppHideInaccessibleEvents' | 'athleteAppAllowInvoiceAccess'
>;

const AthleteAppSettingsCard = ({
  gymData,
  gymDataIsLoading,
  gymDataIsSuccess,
  permission = true,
}: {
  gymData?: GymRetrieve;
  gymDataIsLoading: boolean;
  gymDataIsSuccess: boolean;
  permission?: boolean;
}): JSX.Element => {
  // The API always sends real booleans, so these only stand in while gymData is undefined —
  // behind the LoadingOverlay. They are placeholders, not defaults: the model owns those.
  const initialValues = useMemo((): FormValues => {
    return {
      athleteAppHideReservedAthletes: gymData?.athleteAppHideReservedAthletes ?? false,
      athleteAppHideInaccessibleEvents: gymData?.athleteAppHideInaccessibleEvents ?? false,
      athleteAppAllowInvoiceAccess: gymData?.athleteAppAllowInvoiceAccess ?? false,
    };
  }, [
    gymData?.athleteAppAllowInvoiceAccess,
    gymData?.athleteAppHideInaccessibleEvents,
    gymData?.athleteAppHideReservedAthletes,
  ]);

  const form = useForm<FormValues>({
    initialValues,
  });

  const [isEditable, { open, close }] = useDisclosure(false);

  const updateMutation = useUpdateGym();
  const handleSubmit = (formData: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();

    if (gymData) {
      const request = { ...gymData, gymMailConfig: null, ...formData };

      updateMutation.mutate(request, {
        onSuccess: response => {
          successNotification('Gym settings update success', `${response.data.title} successfully updated.`);
          close();
        },
        onError: error => {
          errorNotification('Gym settings update failed', error.response?.data.message);
        },
      });
    }
  };

  const handleCancelChanges = (): void => {
    form.setValues(initialValues);
    close();
  };

  useShallowEffect(() => {
    if (gymData && gymDataIsSuccess) {
      form.setValues(initialValues);
    }
  }, [initialValues, gymDataIsSuccess]);

  return (
    <Paper shadow='xl' p='sm'>
      <LoadingOverlay visible={gymDataIsLoading} />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Group position='apart' spacing='md' noWrap sx={{ alignItems: 'flex-start' }}>
          <Title order={4}>Athlete App</Title>

          <Group spacing='xs' noWrap position='right'>
            {isEditable ? (
              <>
                <Button
                  color='red'
                  compact
                  onClick={handleCancelChanges}
                  disabled={updateMutation.isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  color='green'
                  compact
                  loading={updateMutation.isLoading}
                  data-cy='gymSettingsAthleteAppSaveButton'
                >
                  Save
                </Button>
              </>
            ) : (
              <Button
                compact
                onClick={open}
                disabled={!permission}
                data-cy='gymSettingsAthleteAppEditButton'
              >
                Edit
              </Button>
            )}
          </Group>
        </Group>

        <Divider mx='-sm' my='sm' />

        <Grid align='center' gutter={6} p='xs'>
          <Switch
            disabled={!isEditable}
            label='Hide list of reserved athletes on events in athlete view'
            checked={form.values.athleteAppHideReservedAthletes}
            {...form.getInputProps('athleteAppHideReservedAthletes')}
            data-cy='gymSettingsHideReservedAthletes'
          />
        </Grid>
        <Grid align='center' gutter={6} p='xs'>
          <Switch
            disabled={!isEditable}
            label='Schedule only shows events that the athlete has access to'
            checked={form.values.athleteAppHideInaccessibleEvents}
            {...form.getInputProps('athleteAppHideInaccessibleEvents')}
            data-cy='gymSettingsHideInaccessibleEvents'
          />
        </Grid>
        <Grid align='center' gutter={6} p='xs'>
          <Switch
            disabled={!isEditable}
            label='Allow athletes to see their invoices on the app'
            checked={form.values.athleteAppAllowInvoiceAccess}
            {...form.getInputProps('athleteAppAllowInvoiceAccess')}
          />
        </Grid>
      </form>
    </Paper>
  );
};

export default AthleteAppSettingsCard;
